/**
 * The document library: a sortable grid with semantic search over it.
 *
 * Columns are deliberately domain-neutral. What a document contains is decided
 * by the prompts, so the grid reports quantities the app can always know - how
 * many regions, how many values, when it was analyzed - and leaves the
 * prompt-defined values to the detail screen.
 */
import { useMemo, useState, useSyncExternalStore } from "react";

import type { DocumentSummary } from "../api";
import { Column, DataGrid, EmptyState, SortState, sortRows } from "../components/DataGrid";
import { Button, Field, Icon } from "../components/primitives";
import { formatDate } from "../format";

interface DocumentsProps {
  documents: DocumentSummary[];
  results: DocumentSummary[] | null;
  query: string;
  searching: boolean;
  /** Rows ticked in the grid: what a comparison compares, and what a delete deletes. */
  selected: string[];
  /** True while an export is being built, so it cannot be started twice. */
  exporting: boolean;
  onQueryChange: (value: string) => void;
  onSearch: () => void;
  onClearSearch: () => void;
  onOpen: (id: string) => void;
  onToggleRow: (id: string) => void;
  onToggleAll: (ids: string[]) => void;
  onClearSelection: () => void;
  onCompare: () => void;
  /** Download every ticked row as one CSV file. */
  onExportSelected: () => void;
}

/** Whether a CSS media query matches, kept current as the window is resized. */
function useMatchMedia(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    () => window.matchMedia(query).matches,
  );
}

export function Documents({
  documents,
  results,
  query,
  searching,
  selected,
  exporting,
  onQueryChange,
  onSearch,
  onClearSearch,
  onOpen,
  onToggleRow,
  onToggleAll,
  onClearSelection,
  onCompare,
  onExportSelected,
}: DocumentsProps) {
  const [sort, setSort] = useState<SortState>({ key: "created", dir: "desc" });
  const narrow = useMatchMedia("(max-width: 720px)");

  const searched = results !== null;
  const rows = searched ? results : documents;

  const columns = useMemo<Column<DocumentSummary>[]>(() => {
    const base: Column<DocumentSummary>[] = [
      {
        key: "thumb",
        label: "",
        width: 54,
        sortable: false,
        render: (row) =>
          row.thumb ? (
            <img className="grid-thumb" src={`data:image/jpeg;base64,${row.thumb}`} alt="" />
          ) : (
            <span className="grid-thumb grid-thumb--empty">
              <Icon name="description" size={16} />
            </span>
          ),
      },
      {
        key: "name",
        label: "Document",
        sortValue: (row) => row.name.toLowerCase(),
        render: (row) => (
          <div style={{ minWidth: 0 }}>
            <a
              className="doc-title u-truncate"
              onClick={(event) => {
                event.stopPropagation();
                onOpen(row.id);
              }}
            >
              {row.name}
            </a>
            <div className="doc-sub">{formatDate(row.created)}</div>
          </div>
        ),
      },
      // Both numbers, because one of them alone misleads. A sheet yields a
      // couple of dozen tight regions, which sounds like a lot until you know
      // they are the parts of eight drawings - and "8 drawings" alone hides how
      // finely each was read.
      {
        key: "n_drawings",
        label: "Drawings",
        width: 86,
        align: "right",
        // The counts and the date go on a phone: the date is already under the
        // name, and four number columns beside it leave the name no room at all.
        className: "col--wide",
        render: (row) =>
          row.n_drawings > 0 ? row.n_drawings : <span className="u-muted">-</span>,
      },
      { key: "n_positions", label: "Regions", width: 84, align: "right", className: "col--wide" },
      { key: "n_fields", label: "Fields", width: 78, align: "right", className: "col--wide" },
      {
        key: "created",
        label: "Analyzed",
        width: 140,
        align: "right",
        mono: true,
        className: "col--wide",
        render: (row) => formatDate(row.created),
      },
    ];

    base.push({
      key: "open",
      label: "",
      width: 40,
      align: "right",
      sortable: false,
      render: () => <Icon name="chevron_right" size={18} className="u-muted" />,
    });

    return base;
    // No longer varies with `searched`: the columns are the same whether the rows
    // came from the library or from a search.
  }, [onOpen]);

  // Search results arrive ranked by similarity; re-sorting them would throw that away.
  const visible = searched ? rows : sortRows(rows, columns, sort);
  const visibleIds = visible.map((row) => row.id);

  return (
    <section className="screen">
      <div className="screen__head">
        <h1 className="screen__title" style={{ flex: 1 }}>
          Documents
        </h1>
        <div className="screen__caption" style={{ paddingBottom: 5 }}>
          {documents.length} documents -{" "}
          {documents.reduce((total, row) => total + row.n_positions, 0)} regions -{" "}
          {documents.reduce((total, row) => total + row.n_fields, 0)} fields
        </div>
      </div>

      <div className="screen__body">
        <div className="grid-wrap">
          <div className="searchbar">
            <Field
              icon="search"
              // The full hint does not fit a phone's field, and a hint cut off
              // mid-word reads as a field that is too small.
              placeholder={
                narrow
                  ? "Search in plain language"
                  : "Search in plain language - what a document means, not the words it uses"
              }
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") onSearch();
              }}
              className="searchbar__field"
              boxClassName=""
            />
            <Button onClick={onSearch} disabled={searching || !query.trim()}>
              {searching ? "Searching..." : "Search"}
            </Button>
            <span className="u-spacer" />
            {selected.length > 0 && (
              <>
                {/*
                  What a tick actually does, said where the ticks are. The three
                  buttons beside it already act on the selection, but the assistant
                  can be pointed at it too and that is not visible from here - so
                  the line names everything the selection feeds rather than leaving
                  "2 selected" to mean whatever the reader assumes.
                */}
                <span className="screen__caption searchbar__selection">
                  {selected.length === 1
                    ? "1 selected - pick one more to compare"
                    : `${selected.length} selected - compare, export or ask about these`}
                </span>
                {selected.length >= 2 && (
                  <Button variant="primary" size="sm" icon="compare_arrows" onClick={onCompare}>
                    Compare ({selected.length})
                  </Button>
                )}
                {/* One file for the whole selection, so what was ticked can be
                    taken straight into a spreadsheet or a script. Offered from a
                    single row too: exporting one document is the same intent, and
                    ticking a row is quicker than opening it. */}
                <Button
                  size="sm"
                  icon="download"
                  onClick={onExportSelected}
                  disabled={exporting}
                >
                  {exporting ? "Exporting..." : `Export CSV (${selected.length})`}
                </Button>
                <Button variant="ghost" size="sm" onClick={onClearSelection}>
                  Clear
                </Button>
              </>
            )}
          </div>

          {searched && (
            <div className="searchnote">
              <span style={{ flex: 1 }}>
                {rows.length} {rows.length === 1 ? "match" : "matches"} for "{query}" - ranked by semantic
                similarity
              </span>
              <a onClick={onClearSearch} style={{ cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                Clear search
              </a>
            </div>
          )}

          {visible.length === 0 ? (
            <EmptyState
              icon={searched ? "search_off" : "description"}
              title={searched ? "No matching documents" : "No drawings in this demo"}
              text={
                searched
                  ? "Nothing in the library is semantically close to that query. Try describing the content differently."
                  : "This demo ships with a fixed set of already-analyzed drawings, and none are loaded. Analyzing your own uploads is part of the full solution."
              }
              action={searched ? <Button onClick={onClearSearch}>Clear search</Button> : undefined}
            />
          ) : (
            <div className="grid-scroll">
              <DataGrid
                columns={columns}
                rows={visible}
                rowKey={(row) => row.id}
                selected={selected}
                onToggleRow={onToggleRow}
                onToggleAll={() => onToggleAll(visibleIds)}
                sort={searched ? undefined : sort}
                onSort={(key) =>
                  setSort((current) => ({
                    key,
                    dir: current.key === key && current.dir === "asc" ? "desc" : "asc",
                  }))
                }
                onRowClick={(row) => onOpen(row.id)}
              />
            </div>
          )}

          <div className="dac-gridfoot">
            <span>
              {searched ? `${rows.length} of ${documents.length}` : documents.length} documents
            </span>
            <span className="u-spacer" />
          </div>
        </div>
      </div>
    </section>
  );
}
